import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Spinner, Alert, Input
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";
import { downloadObjectsCsv } from "../../../helpers/csvExport";

const BASE_URL = API_BASE;

/**
 * Cüzdan hareketleri — para çekme / iade / bakiye yükleme işlemlerinin
 * denetim izi. Bakiye tutmayan bir tartışmayı ancak bu kayıtlarla çözersiniz.
 *
 * ⚠️ KULLANICI_CUZDAN_HAREKETLERI tablosu OData modelinde tanımlı ama
 *    EntitySet olarak YAYINLANMAMIŞ (istek 404 döner). Backend'de
 *    WebApiConfig içine bir satır eklenmesi gerekir:
 *
 *      builder.EntitySet<KULLANICI_CUZDAN_HAREKETLERI>("KULLANICI_CUZDAN_HAREKETLERI");
 *
 *    O satır eklenene kadar bu sayfa nedenini açıklayan bir uyarı gösterir.
 */

const ISLEM_TIPLERI = {
  IADE: { color: "success", label: "İade" },
  PARA_CEKME_IADE: { color: "success", label: "Para Çekme İadesi" },
  PARA_CEKME_ONAY: { color: "danger", label: "Para Çekme" },
  PARA_CEKME_RED: { color: "secondary", label: "Para Çekme Reddi" },
  IADE_RED: { color: "secondary", label: "İade Reddi" },
  IADE_DURUM: { color: "info", label: "İade Durum" },
  YUKLEME: { color: "primary", label: "Bakiye Yükleme" },
  HARCAMA: { color: "warning", label: "Harcama" },
};

const CuzdanHareketleri = () => {
  document.title = "Cüzdan Hareketleri | HarmanaGEL";

  const [hareketler, setHareketler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [yayinlanmamis, setYayinlanmamis] = useState(false);
  const [tipFiltre, setTipFiltre] = useState("");

  const fetchHareketler = async () => {
    setIsLoading(true);
    setError(null);
    setYayinlanmamis(false);
    try {
      const res = await fetch(
        `${BASE_URL}/KULLANICI_CUZDAN_HAREKETLERI?$top=2000&$orderby=ISLEM_TARIHI desc` +
          `&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress)`,
        { headers: getAuthHeaders(), cache: "no-store" }
      );
      if (res.status === 404) {
        setYayinlanmamis(true);
        setHareketler([]);
        return;
      }
      if (!res.ok) throw new Error(`Yükleme hatası (${res.status})`);
      const result = await res.json();
      setHareketler(result?.value || []);
    } catch (err) {
      setError(err.message || "Cüzdan hareketleri yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchHareketler(); }, []);

  const filtreli = useMemo(
    () => (tipFiltre ? hareketler.filter((h) => h.ISLEM_TIPI === tipFiltre) : hareketler),
    [hareketler, tipFiltre]
  );

  const { girisToplam, cikisToplam } = useMemo(() => {
    let giris = 0;
    let cikis = 0;
    filtreli.forEach((h) => {
      const m = Number(h.MIKTAR) || 0;
      if (m >= 0) giris += m;
      else cikis += Math.abs(m);
    });
    return { girisToplam: giris, cikisToplam: cikis };
  }, [filtreli]);

  const mevcutTipler = useMemo(
    () => [...new Set(hareketler.map((h) => h.ISLEM_TIPI).filter(Boolean))].sort(),
    [hareketler]
  );

  const tipBadge = (tip) => {
    const cfg = ISLEM_TIPLERI[tip] || { color: "secondary", label: tip || "-" };
    return <Badge color={cfg.color}>{cfg.label}</Badge>;
  };

  const exportCsv = () =>
    downloadObjectsCsv(
      `cuzdan-hareketleri-${new Date().toISOString().slice(0, 10)}.csv`,
      filtreli,
      [
        { key: "ID", label: "ID" },
        { key: "KULLANICI_ID", label: "Kullanıcı ID" },
        { label: "Ad Soyad", get: (h) => h.KULLANICILAR?.AD_SOYAD || "" },
        { key: "ISLEM_TIPI", label: "İşlem Tipi" },
        { key: "MIKTAR", label: "Miktar" },
        { key: "ACIKLAMA", label: "Açıklama" },
        { key: "SIPARIS_ID", label: "Sipariş ID" },
        { key: "DURUM", label: "Durum" },
        {
          label: "İşlem Tarihi",
          get: (h) => (h.ISLEM_TARIHI ? new Date(h.ISLEM_TARIHI).toLocaleString("tr-TR") : ""),
        },
      ]
    );

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Kullanıcı",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : (
          <span className="text-muted">#{cell.row.original.KULLANICI_ID}</span>
        );
      },
    },
    {
      header: "İşlem Tipi",
      accessorKey: "ISLEM_TIPI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => tipBadge(cell.getValue()),
    },
    {
      header: "Miktar",
      accessorKey: "MIKTAR",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const m = Number(cell.getValue()) || 0;
        if (m === 0) return <span className="text-muted">—</span>;
        return (
          <span className={`fw-bold ${m > 0 ? "text-success" : "text-danger"}`}>
            {m > 0 ? "+" : "−"}
            {Math.abs(m).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
          </span>
        );
      },
    },
    {
      header: "Açıklama",
      accessorKey: "ACIKLAMA",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <span
          style={{ maxWidth: 320, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={cell.getValue()}
        >
          {cell.getValue() || "-"}
        </span>
      ),
    },
    {
      header: "Sipariş",
      accessorKey: "SIPARIS_ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (cell.getValue() ? `#${cell.getValue()}` : "-"),
    },
    {
      header: "Tarih",
      accessorKey: "ISLEM_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleString("tr-TR") : "-";
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Cüzdan Hareketleri" />

          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {yayinlanmamis && (
            <Alert color="warning">
              <h6 className="alert-heading">
                <i className="bx bx-error me-1" />
                Tablo API'de yayınlanmamış
              </h6>
              <p className="mb-2 small">
                <code>KULLANICI_CUZDAN_HAREKETLERI</code> tablosu veritabanı modelinde
                tanımlı ancak OData servisinde <strong>EntitySet olarak açılmamış</strong>,
                bu yüzden istek 404 dönüyor. Backend'de <code>WebApiConfig.cs</code>
                içindeki EDM kurulumuna tek satır eklenmesi yeterli:
              </p>
              <pre className="mb-2 p-2 bg-light border rounded small">
{`builder.EntitySet<KULLANICI_CUZDAN_HAREKETLERI>("KULLANICI_CUZDAN_HAREKETLERI");`}
              </pre>
              <p className="mb-0 small text-muted">
                Bu satır eklendiğinde sayfa hiçbir değişiklik gerekmeden çalışmaya başlar.
              </p>
            </Alert>
          )}

          {!yayinlanmamis && hareketler.length > 0 && (
            <Row className="mb-2">
              <Col md={4}>
                <Card className="mini-stats-wid mb-3">
                  <CardBody className="py-3">
                    <p className="text-muted mb-1 font-size-12">Toplam Giriş</p>
                    <h5 className="mb-0 text-success">
                      +{girisToplam.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                    </h5>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="mini-stats-wid mb-3">
                  <CardBody className="py-3">
                    <p className="text-muted mb-1 font-size-12">Toplam Çıkış</p>
                    <h5 className="mb-0 text-danger">
                      −{cikisToplam.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                    </h5>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="mini-stats-wid mb-3">
                  <CardBody className="py-3">
                    <p className="text-muted mb-1 font-size-12">Kayıt Sayısı</p>
                    <h5 className="mb-0">{filtreli.length}</h5>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h4 className="card-title mb-0">Cüzdan Hareketleri</h4>
                    <div className="d-flex gap-2 align-items-center">
                      {mevcutTipler.length > 0 && (
                        <Input
                          type="select"
                          bsSize="sm"
                          style={{ width: 200 }}
                          value={tipFiltre}
                          onChange={(e) => setTipFiltre(e.target.value)}
                        >
                          <option value="">Tüm işlem tipleri</option>
                          {mevcutTipler.map((t) => (
                            <option key={t} value={t}>
                              {ISLEM_TIPLERI[t]?.label || t}
                            </option>
                          ))}
                        </Input>
                      )}
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={exportCsv}
                        disabled={!filtreli.length}
                      >
                        <i className="bx bx-export me-1" />
                        CSV
                      </Button>
                      <Button color="primary" size="sm" onClick={fetchHareketler} disabled={isLoading}>
                        <i className="bx bx-refresh me-1" />
                        Yenile
                      </Button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={filtreli}
                      isGlobalFilter={true}
                      SearchPlaceholder="Hareket ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CuzdanHareketleri;
